/**
 * Created by neo on 10.01.2026
 * Challenge Location Modal - Shows detailed location info with locked/reached states
 */
import * as React from 'react';
import { useMemo } from 'react';
import { observer } from 'mobx-react';
import styled from '@emotion/styled';
import dayjs from 'dayjs';
import { Modal, ModalBody } from 'reactstrap';
import { useTrans } from '../../../Store/System/LocalizationStore';
import { useLocalized } from '../../../Hooks/useLocalized';
import { RouteChallengeLocation } from '../../../Model/Engagement/RouteChallenge/RouteChallengeLocation';
import { RouteChallengeTeamReachedLocation } from '../../../Model/Engagement/RouteChallenge/RouteChallengeTeamReachedLocation';
import { Theme } from '../../../Theme/Theme';
import { getMediaLarge, getMediaMedium, getMediaSmallest } from '../../../Model/Media/Media';
import { MarkdownRenderer } from '../../../Components/Markdown/MarkdownRenderer';
import { ChallengeType } from '../ChallengeDetailScreen';

const ModalContainer = styled(Modal)`
  max-width: 600px;

  .modal-content {
    border-radius: 20px;
    overflow: hidden;
    border: none;
  }
`;

const CloseButton = styled.button`
  position: absolute;
  top: 16px;
  right: 16px;
  z-index: 10;
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.9);
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 20px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);

  &:hover {
    background: white;
  }
`;

const HeroImage = styled.div<{ imageUrl?: string }>`
  position: relative;
  width: 100%;
  height: 200px;
  background: ${(props) =>
    props.imageUrl
      ? `url(${props.imageUrl})`
      : `linear-gradient(135deg, ${Theme.Colors.primary} 0%, ${Theme.Colors.primaryDark} 100%)`};
  background-size: cover;
  background-position: center;

  &::after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    height: 50%;
    background: linear-gradient(to top, rgba(0, 0, 0, 0.6) 0%, transparent 100%);
  }
`;

const HeroContent = styled.div`
  position: absolute;
  bottom: 16px;
  left: 16px;
  right: 16px;
  z-index: 5;
  color: white;
`;

const LocationBadge = styled.span<{ isFirst?: boolean; isLast?: boolean; isReached?: boolean }>`
  display: inline-block;
  padding: 4px 12px;
  border-radius: 12px;
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  margin-bottom: 8px;
  background: ${(props) => {
    if (props.isFirst) return '#52c41a';
    if (props.isLast) return '#f5222d';
    if (props.isReached) return Theme.Colors.primary;
    return 'rgba(255, 255, 255, 0.2)';
  }};
`;

const LocationTitle = styled.h2`
  font-size: 24px;
  font-weight: 700;
  margin: 0;
  color: white;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.3);
`;

const ContentSection = styled.div`
  padding: 24px;
`;

const StatusCard = styled.div<{ variant: 'locked' | 'reached' | 'progress' }>`
  border-radius: 16px;
  padding: 20px;
  margin-bottom: 20px;
  background: ${(props) => {
    switch (props.variant) {
      case 'reached':
        return `linear-gradient(135deg, ${Theme.Colors.primaryDarkest} 0%, ${Theme.Colors.primaryDark} 100%)`;
      case 'progress':
        return Theme.Colors.lightestGrey;
      case 'locked':
      default:
        return '#F5F5F5';
    }
  }};
  color: ${(props) => (props.variant === 'reached' ? 'white' : Theme.Colors.textColor)};
`;

const StatusIconContainer = styled.div`
  width: 64px;
  height: 64px;
  border-radius: 32px;
  background: ${(props) => props.color || 'white'};
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 16px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
`;

const StatusIcon = styled.span`
  font-size: 28px;
`;

const StatusTitle = styled.h3`
  font-size: 18px;
  font-weight: 700;
  text-align: center;
  margin: 0 0 8px 0;
`;

const StatusDescription = styled.p`
  font-size: 14px;
  text-align: center;
  margin: 0;
  opacity: 0.9;
  line-height: 1.5;
`;

const ProgressSection = styled.div`
  background: white;
  border-radius: 12px;
  padding: 16px;
  margin-top: 16px;
`;

const ProgressHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
`;

const ProgressLabel = styled.span`
  font-size: 13px;
  color: ${Theme.Colors.charcoal};
`;

const ProgressValue = styled.span`
  font-size: 14px;
  font-weight: 700;
  color: ${Theme.Colors.textColor};
`;

const ProgressBarWrapper = styled.div`
  height: 10px;
  border-radius: 5px;
  background: ${Theme.Colors.lightestGrey};
  overflow: hidden;
`;

const ProgressBarFill = styled.div<{ progress: number }>`
  height: 100%;
  border-radius: 5px;
  background: linear-gradient(90deg, ${Theme.Colors.primary} 0%, ${Theme.Colors.primaryDark} 100%);
  width: ${(props) => Math.min(props.progress, 100)}%;
  transition: width 0.5s ease-out;
`;

const ReachedDateContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  background: rgba(255, 255, 255, 0.15);
  border-radius: 12px;
  padding: 12px;
  margin-top: 16px;
`;

const ReachedDateIcon = styled.span`
  font-size: 18px;
`;

const ReachedDateText = styled.span`
  font-size: 13px;
  opacity: 0.9;
`;

const DescriptionSection = styled.div`
  margin-bottom: 20px;

  h1,
  h2,
  h3 {
    font-size: 18px;
    font-weight: 600;
    margin: 16px 0 8px;
  }

  p {
    font-size: 14px;
    line-height: 1.6;
    color: ${Theme.Colors.charcoal};
    margin: 0 0 12px;
  }

  a {
    color: ${Theme.Colors.primary};
  }

  ul,
  ol {
    padding-left: 20px;
    margin: 0 0 12px;
  }

  li {
    font-size: 14px;
    line-height: 1.6;
    color: ${Theme.Colors.charcoal};
  }
`;

const MediaGallery = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(150px, 1fr));
  gap: 12px;
  margin-top: 20px;
`;

const MediaItem = styled.img`
  width: 100%;
  height: 120px;
  object-fit: cover;
  border-radius: 12px;
  cursor: pointer;
  transition: transform 0.2s;

  &:hover {
    transform: scale(1.03);
  }
`;

const LockedContentOverlay = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px 20px;
  text-align: center;
`;

const LockedIcon = styled.span`
  font-size: 48px;
  margin-bottom: 16px;
`;

const LockedTitle = styled.h3`
  font-size: 18px;
  font-weight: 600;
  margin: 0 0 8px;
  color: ${Theme.Colors.textColor};
`;

const LockedDescription = styled.p`
  font-size: 14px;
  color: ${Theme.Colors.charcoal};
  margin: 0;
  line-height: 1.5;
`;

const PointsBadge = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: ${Theme.Colors.lightestGrey};
  padding: 8px 16px;
  border-radius: 20px;
  margin-top: 16px;
  font-size: 14px;
  font-weight: 600;
  color: ${Theme.Colors.charcoal};
`;

export type ChallengeLocationModalProps = {
  isOpen: boolean;
  onClose: () => void;
  location: RouteChallengeLocation | null;
  locationIndex: number;
  totalLocations: number;
  language: string;
  challengeType: ChallengeType;
  reachedLocations?: RouteChallengeTeamReachedLocation[];
  myTotalPoints?: number;
  hasJoined?: boolean;
  challengeStarted?: boolean;
};

export const ChallengeLocationModal: React.FC<ChallengeLocationModalProps> = observer(
  ({
    isOpen,
    onClose,
    location,
    locationIndex,
    totalLocations,
    language,
    challengeType,
    reachedLocations = [],
    myTotalPoints = 0,
    hasJoined = false,
    challengeStarted = false,
  }) => {
    const { t } = useTrans();

    if (!location) return null;

    const isFirst = locationIndex === 0;
    const isLast = locationIndex === totalLocations - 1;
    const reachedInfo = reachedLocations.find((r) => r.locationId === location.id);
    // First location is always considered "reached" after challenge starts
    const isReached = !!reachedInfo || (isFirst && challengeStarted);

    const locationName = useLocalized(location.name) || `Location ${locationIndex + 1}`;
    const locationDescription = useLocalized(location.description);
    const locationImage = location.image ? (getMediaLarge(location.image) || getMediaMedium(location.image)) : undefined;

    const previousLocationIndex = locationIndex > 0 ? locationIndex - 1 : -1;
    const previousLocationTotalRequired =
      previousLocationIndex >= 0 ? location.totalPointsRequired - location.pointsRequired : 0;
    const pointsSinceLastLocation = Math.max(0, myTotalPoints - previousLocationTotalRequired);
    const progress = location.pointsRequired > 0 ? (pointsSinceLastLocation / location.pointsRequired) * 100 : 0;

    const reachedDateTime = reachedInfo?.dateTime;
    const reachedDateFormatted = useMemo(() => {
      if (!reachedDateTime) return null;
      return dayjs(reachedDateTime).format('LLLL');
    }, [reachedDateTime]);

    const reachedLocationIds = useMemo(
      () => new Set((reachedLocations || []).map((r) => r.locationId)),
      [reachedLocations],
    );
    const nextUnreachedIndex = Array.from({ length: totalLocations }).findIndex(
      (_, i) => !reachedLocationIds.has(location.id),
    );
    const isNextLocation = locationIndex === nextUnreachedIndex || isFirst;

    const renderContent = () => {
      if (isFirst || isReached) {
        return (
          <>
            {isReached && (
              <StatusCard variant="reached">
                <StatusIconContainer color="rgba(255,255,255,0.2)">
                  <StatusIcon>🎉</StatusIcon>
                </StatusIconContainer>
                <StatusTitle>{t('challenge.location.reached', { defaultValue: 'Location Reached!' })}</StatusTitle>
                <StatusDescription>
                  {challengeType === 'individual'
                    ? t('challenge.location.congratulations', { defaultValue: 'Congratulations!' })
                    : t('challenge.location.teamSuccess', { defaultValue: 'Your team made it here!' })}
                </StatusDescription>
                {reachedDateFormatted && (
                  <ReachedDateContainer>
                    <ReachedDateIcon>📅</ReachedDateIcon>
                    <ReachedDateText>
                      {t('challenge.location.reachedOn', {
                        defaultValue: 'Reached on {{date}}',
                        date: reachedDateFormatted,
                      })}
                    </ReachedDateText>
                  </ReachedDateContainer>
                )}
              </StatusCard>
            )}

            {locationDescription && (
              <DescriptionSection>
                <MarkdownRenderer text={locationDescription} />
              </DescriptionSection>
            )}

            {location.medias && location.medias.length > 0 && (
              <MediaGallery>
                {location.medias.map((media, idx) => (
                  <MediaItem
                    key={media.id || idx}
                    src={getMediaMedium(media) || getMediaSmallest(media)}
                    alt={`${locationName} media ${idx + 1}`}
                  />
                ))}
              </MediaGallery>
            )}
          </>
        );
      }

      if (!hasJoined) {
        return (
          <StatusCard variant="locked">
            <LockedContentOverlay>
              <LockedIcon>🔒</LockedIcon>
              <LockedTitle>{t('challenge.location.locked', { defaultValue: 'Location Locked' })}</LockedTitle>
              <LockedDescription>
                {challengeType === 'individual'
                  ? t('challenge.location.joinToUnlock', {
                      defaultValue: 'Join the challenge and collect points to unlock this destination.',
                    })
                  : t('challenge.location.joinTeamToUnlock', {
                      defaultValue: 'Join a team and collect points together to unlock this destination.',
                    })}
              </LockedDescription>
              <PointsBadge>
                ⭐ {location.totalPointsRequired.toLocaleString()}{' '}
                {t('challenge.location.pointsNeeded', { defaultValue: 'points needed' })}
              </PointsBadge>
            </LockedContentOverlay>
          </StatusCard>
        );
      }

      if (!challengeStarted) {
        return (
          <StatusCard variant="locked">
            <LockedContentOverlay>
              <LockedIcon>⏳</LockedIcon>
              <LockedTitle>{t('challenge.location.comingSoon', { defaultValue: 'Coming Soon' })}</LockedTitle>
              <LockedDescription>
                {t('challenge.location.challengeNotStarted', {
                  defaultValue: 'The adventure begins soon! Get ready to collect points and unlock this destination.',
                })}
              </LockedDescription>
            </LockedContentOverlay>
          </StatusCard>
        );
      }

      if (isNextLocation) {
        return (
          <StatusCard variant="progress">
            <StatusIconContainer color={Theme.Colors.lightestPrimary}>
              <StatusIcon>🎯</StatusIcon>
            </StatusIconContainer>
            <StatusTitle>{t('challenge.location.inProgress', { defaultValue: 'Next Destination' })}</StatusTitle>
            <StatusDescription>
              {t('challenge.location.keepGoing', {
                defaultValue: 'Keep collecting points to unlock this location!',
              })}
            </StatusDescription>

            <ProgressSection>
              <ProgressHeader>
                <ProgressLabel>{t('challenge.location.progress', { defaultValue: 'Progress' })}</ProgressLabel>
                <ProgressValue>
                  {pointsSinceLastLocation.toLocaleString()} / {location.pointsRequired.toLocaleString()}
                </ProgressValue>
              </ProgressHeader>
              <ProgressBarWrapper>
                <ProgressBarFill progress={progress} />
              </ProgressBarWrapper>
            </ProgressSection>
          </StatusCard>
        );
      }

      return (
        <StatusCard variant="locked">
          <LockedContentOverlay>
            <LockedIcon>🎯</LockedIcon>
            <LockedTitle>
              {t('challenge.location.futureDestination', { defaultValue: 'Future Destination' })}
            </LockedTitle>
            <LockedDescription>
              {t('challenge.location.keepProgressing', {
                defaultValue: 'Keep progressing through the challenge to unlock this destination.',
              })}
            </LockedDescription>
            <PointsBadge>
              ⭐ {location.totalPointsRequired.toLocaleString()}{' '}
              {t('challenge.location.totalPointsNeeded', { defaultValue: 'total points needed' })}
            </PointsBadge>
          </LockedContentOverlay>
        </StatusCard>
      );
    };

    return (
      <ModalContainer isOpen={isOpen} toggle={onClose} centered>
        <HeroImage imageUrl={locationImage}>
          <CloseButton onClick={onClose}>×</CloseButton>
          <HeroContent>
            <LocationBadge isFirst={isFirst} isLast={isLast} isReached={isReached}>
              {isFirst
                ? t('challenge.location.start', { defaultValue: 'Start' })
                : isLast
                  ? t('challenge.location.finish', { defaultValue: 'Finish' })
                  : `#${locationIndex + 1}`}
            </LocationBadge>
            <LocationTitle>{locationName}</LocationTitle>
          </HeroContent>
        </HeroImage>

        <ModalBody>
          <ContentSection>{renderContent()}</ContentSection>
        </ModalBody>
      </ModalContainer>
    );
  },
);
